import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  Collapse,
  IconButton,
  Badge,
  Button,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel"; // New icon for cancelled status
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getOrders, getOrdersCounter } from "../redux/slices/order/thunk";
import LoadingComponent from "../components/LoadingComponent";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CreditCardIcon from "@mui/icons-material/CreditCard";

const MyOrders = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallMobile = useMediaQuery("(max-width:400px)");
  const navigate = useNavigate();
  const isRtl = i18n.dir() === "rtl";
  const [expandedOrders, setExpandedOrders] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const user = JSON.parse(localStorage.getItem("user"));
  const { orders } = useSelector((state) => state.orderSlice);

  const [loading,setLoading]=useState(false);
    const dispatch = useDispatch();

  useEffect(() => {
  const fetchOrders = async () => {
    setLoading(true);
    try{
      await dispatch(getOrders({ id: user?._id }));
      await dispatch(getOrdersCounter({ id: user?._id, type: user?.type, status: "pending" }));
    }
    catch(error){
      console.log("error",error);
    }finally{
      setLoading(false);
    }
  };

  fetchOrders();
}, []);


  // Transform orders to match frontend structure
  const transformOrders = (orders) => {
    return orders.map((order) => ({
      orderId: order.orderNumber,
      orderDate: order.createdAt,
      orderStatus: order.orderStatus,
      totalPrice: order.totalAmount,
      families: order.ordersFamily.map((familyOrder) => ({
        familyId: familyOrder.familyId?._id || "",
        familyName: familyOrder.familyId?.userName || t("orders.unknownFamily"),
        familyAddress: familyOrder.familyId?.address || "",
        familyLocation: familyOrder.familyId?.location || "",
        familyBankAccountNumber: familyOrder.familyId?.bankAccountNumber || "",
        familyImage: familyOrder.familyId?.image || "",
        orderStatus: familyOrder.orderStatus,
        products: familyOrder.products.map((product) => ({
          id: product.productId?._id || "",
          name: product.productId?.name || t("orders.unknownProduct"),
          image: product.productId?.image || "",
          description: product.productId?.description || "",
          price: product.priceAfterDiscount,
          quantity: product.quantity,
        })),
      })),
    }));
  };

  const transformedOrders = orders ? transformOrders(orders) : [];

  // Filter orders based on status
  const newOrders = transformedOrders.filter(
    (order) =>
      order.orderStatus === "pending" || order.orderStatus === "in-transit"
  );

  const deliveredOrders = transformedOrders.filter(
    (order) => order.orderStatus === "delivered" || order.orderStatus === "cancelled"
  );

  const cancelledOrders = false;
  // const cancelledOrders = transformedOrders.filter(
  //   (order) => order.orderStatus === "cancelled"
  // );

  // Check if we should show the cancelled orders tab
  const showCancelledTab = cancelledOrders.length > 0;

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const toggleOrder = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return <CheckCircleIcon color="success" sx={{ ml: 1 }} />;
      case "in-transit":
        return <LocalShippingIcon color="primary" sx={{ ml: 1 }} />;
      case "pending":
        return <AccessTimeIcon color="warning" sx={{ ml: 1 }} />;
      case "cancelled":
        return <CancelIcon color="error" sx={{ ml: 1 }} />;
      default:
        return <AccessTimeIcon />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "delivered":
        return t("orders.statusDelivered");
      case "in-transit":
        return t("orders.statusInTransit");
      case "pending":
        return t("orders.statusPending");
      case "cancelled":
        return t("orders.statusCancelled");
      default:
        return t("orders.statusUnknown");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "success.main";
      case "in-transit":
        return "primary.main";
      case "pending":
        return "warning.main";
      case "cancelled":
        return "error.main";
      default:
        return "text.secondary";
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  console.log("loading",loading);
    if (loading) {
    return <LoadingComponent />;
  }

  if (user?.type !== "client") {
    return <Navigate to="/" replace />;
  }


  return (
    <Box
      sx={{
        px: isMobile ? 1 : 4,
        py: 2,
        direction: isRtl ? "rtl" : "ltr",
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.grey[900]
            : theme.palette.grey[50],
        minHeight: "100vh",
      }}
    >
      <Typography
        variant={isMobile ? "h5" : "h4"}
        component="h1"
        gutterBottom
        sx={{
          fontWeight: 700,
          mb: 3,
          color: theme.palette.primary.main,
          textAlign: "center",
        }}
      >
        {t("myOrders.title")}
      </Typography>

      {/* Tabs for New/Delivered/Cancelled Orders */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "fullWidth"}
          textColor="primary"
          indicatorColor="primary"
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
        >
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <Badge
                  // badgeContent={newOrders.length}
                  color="primary"
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: isRtl ? "left" : "right",
                  }}
                  sx={{
                    "& .MuiBadge-badge": {
                      minWidth: isSmallMobile ? 22 : 30,
                      height: isSmallMobile ? 22 : 30,
                      fontSize: isSmallMobile ? "0.75rem" : "1rem",
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                  }}
                >
                  <Typography variant="body1">
                    {t("myOrders.newOrders")}
                  </Typography>
                </Badge>
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <Badge
                  // badgeContent={deliveredOrders.length}
                  color="success"
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: isRtl ? "left" : "right",
                  }}
                  sx={{
                    "& .MuiBadge-badge": {
                      minWidth: isSmallMobile ? 22 : 30,
                      height: isSmallMobile ? 22 : 30,
                      fontSize: isSmallMobile ? "0.75rem" : "1rem",
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                  }}
                >
                  <Typography variant="body1">
                    {t("myOrders.deliveredOrders")}
                  </Typography>
                </Badge>
              </Stack>
            }
          />

          {/* Conditionally render cancelled orders tab */}
          {showCancelledTab && (
            <Tab
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Badge
                    // badgeContent={cancelledOrders.length}
                    color="error"
                    overlap="circular"
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: isRtl ? "left" : "right",
                    }}
                    sx={{
                      "& .MuiBadge-badge": {
                        minWidth: isSmallMobile ? 22 : 30,
                        height: isSmallMobile ? 22 : 30,
                        fontSize: isSmallMobile ? "0.75rem" : "1rem",
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Typography variant="body1">
                      {t("myOrders.cancelledOrders")}
                    </Typography>
                  </Badge>
                </Stack>
              }
            />
          )}
        </Tabs>
      </Box>

      {/* Empty states */}
      {tabValue === 0 && newOrders.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: isMobile ? 5 : 10,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: theme.shadows[1],
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("myOrders.noNewOrders")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            {t("myOrders.noNewOrdersDesc")}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              navigate("/home");
            }}
            color="primary"
          >
            {t("myOrders.browseProducts")}
          </Button>
        </Box>
      )}

      {tabValue === 1 && deliveredOrders.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: isMobile ? 5 : 10,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: theme.shadows[1],
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("myOrders.noDeliveredOrders")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            {t("myOrders.noDeliveredOrdersDesc")}
          </Typography>
        </Box>
      )}

      {tabValue === 2 && cancelledOrders.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: isMobile ? 5 : 10,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: theme.shadows[1],
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("myOrders.noCancelledOrders")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            {t("myOrders.noCancelledOrdersDesc")}
          </Typography>
        </Box>
      )}

      {/* Render orders based on selected tab */}
      {tabValue === 0 &&
        newOrders.map((order) => (
          <OrderCard
            key={order.orderId}
            order={order}
            isMobile={isMobile}
            isSmallMobile={isSmallMobile}
            isRtl={isRtl}
            theme={theme}
            t={t}
            expandedOrders={expandedOrders}
            toggleOrder={toggleOrder}
            formatDate={formatDate}
            getStatusIcon={getStatusIcon}
            getStatusText={getStatusText}
            getStatusColor={getStatusColor}
          />
        ))}

      {tabValue === 1 &&
        deliveredOrders.map((order) => (
          <OrderCard
            key={order.orderId}
            order={order}
            isMobile={isMobile}
            isSmallMobile={isSmallMobile}
            isRtl={isRtl}
            theme={theme}
            t={t}
            expandedOrders={expandedOrders}
            toggleOrder={toggleOrder}
            formatDate={formatDate}
            getStatusIcon={getStatusIcon}
            getStatusText={getStatusText}
            getStatusColor={getStatusColor}
          />
        ))}

      {tabValue === 2 &&
        cancelledOrders.map((order) => (
          <OrderCard
            key={order.orderId}
            order={order}
            isMobile={isMobile}
            isSmallMobile={isSmallMobile}
            isRtl={isRtl}
            theme={theme}
            t={t}
            expandedOrders={expandedOrders}
            toggleOrder={toggleOrder}
            formatDate={formatDate}
            getStatusIcon={getStatusIcon}
            getStatusText={getStatusText}
            getStatusColor={getStatusColor}
          />
        ))}
    </Box>
  );
};

// Extracted Order Card component for better readability
const OrderCard = ({
  order,
  isMobile,
  isSmallMobile,
  isRtl,
  theme,
  t,
  expandedOrders,
  toggleOrder,
  formatDate,
  getStatusIcon,
  getStatusText,
  getStatusColor,
}) => (
  <Paper
    sx={{
      p: isMobile ? 1 : 3,
      mb: isMobile ? 2 : 4,
      borderRadius: 2,
      boxShadow: theme.shadows[3],
      borderLeft: `4px solid ${getStatusColor(order.orderStatus)}`,
      backgroundColor: theme.palette.background.paper,
    }}
    elevation={3}
  >
    {/* Order Summary Header */}
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "center",
        cursor: "pointer",
        p: isMobile ? 0.5 : 1,
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
          borderRadius: 1,
        },
      }}
      onClick={() => toggleOrder(order.orderId)}
    >
      <Box sx={{ mb: isMobile ? 1 : 0 }}>
        <Typography variant="body2" color="text.secondary">
          {t("orders.orderNumber")}
        </Typography>
        <Typography
          variant={isMobile ? "body1" : "h6"}
          fontWeight="bold"
          color="primary"
        >
          {order.orderId}
        </Typography>
      </Box>

      <Box sx={{ mb: isMobile ? 1 : 0 }}>
        <Typography variant="body2" color="text.secondary">
          {t("orders.orderDate")}
        </Typography>
        <Typography variant={isMobile ? "body2" : "body1"}>
          {formatDate(order.orderDate)}
        </Typography>
      </Box>

      <Box sx={{ mb: isMobile ? 1 : 0 }}>
        <Typography variant="body2" color="text.secondary">
          {t("orders.totalPrice")}
        </Typography>
        <Typography
          variant={isMobile ? "body2" : "body1"}
          fontWeight="bold"
          color="text.primary"
        >
          {order.totalPrice?.toFixed(2)} <span>{t("rs")}</span>
        </Typography>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: isMobile ? 1 : 0 }}
      >
        <Box>{getStatusIcon(order.orderStatus)}</Box>
        <Typography
          variant={isMobile ? "body2" : "body1"}
          fontWeight="bold"
          sx={{ color: getStatusColor(order.orderStatus) }}
        >
          {getStatusText(order.orderStatus)}
        </Typography>
        <IconButton size={isMobile ? "small" : "medium"}>
          {expandedOrders[order.orderId] ? (
            <ExpandLessIcon />
          ) : (
            <ExpandMoreIcon />
          )}
        </IconButton>
      </Stack>
    </Box>

    {/* Family Details - Collapsible */}
    <Collapse in={expandedOrders[order.orderId]}>
      <Divider sx={{ my: 1 }} />

      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {t("orders.families")}:
        </Typography>
      </Box>

      {order.families.map((family) => (
        <Box key={family.familyId} sx={{ mb: 2 }}>
         <Box
  sx={{
    display: "flex",
    flexDirection: "column",
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.grey[900]
        : theme.palette.grey[50],
    p: isMobile ? 1 : 2,
    borderRadius: 1,
    mb: 1,
    gap: 1,
  }}
>
  {/* الصف الأول: الصورة + الاسم + الحالة + زر التواصل */}
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      flexWrap: "wrap",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
      <Badge
        badgeContent={family?.products?.length}
        color="primary"
        overlap="circular"
        anchorOrigin={{
          vertical: "top",
          horizontal: isRtl ? "left" : "right",
        }}
        sx={{
          "& .MuiBadge-badge": {
            minWidth: isSmallMobile ? 22 : 30,
            height: isSmallMobile ? 22 : 30,
            fontSize: isSmallMobile ? "0.75rem" : "1rem",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
        }}
      >
        <Avatar
          src={family.familyImage}
          sx={{
            width: isSmallMobile ? 36 : 50,
            height: isSmallMobile ? 36 : 50,
          }}
          variant="circular"
        />
      </Badge>

      <Typography
        variant={isMobile ? "body1" : "subtitle1"}
        fontWeight="bold"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {family.familyName}
      </Typography>
    </Box>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Box
        sx={{
          color: getStatusColor(family.orderStatus),
          borderRadius: 10,
          px: 1,
          py: 0.3,
          fontSize: isMobile ? "0.65rem" : "0.75rem",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {getStatusIcon(family.orderStatus)}
        {getStatusText(family.orderStatus)}
      </Box>

      <Button
        variant="outlined"
        size="small"
        sx={{
          minWidth: "unset",
          fontSize: isMobile ? "0.75rem" : "0.875rem",
        }}
      >
        {t("orders.contactFamily")}
      </Button>
    </Box>
  </Box>

  {/* الصف الثاني: معلومات إضافية */}
  <Box
    sx={{
      display: "flex",
      flexDirection: isSmallMobile ? "column" : "row",
      gap: 2,
      mt: 1,
      flexWrap: "wrap",
    }}
  >
    {family.familyAddress && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <HomeIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">
          {family.familyAddress}
        </Typography>
      </Box>
    )}

    {family.familyLocation && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <LocationOnIcon fontSize="small" color="action" />
        <Typography
          variant="body2"
          component="a"
          href={family.familyLocation}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {t("userForm.fields.location")}
        </Typography>
      </Box>
    )}

    {family.familyBankAccountNumber && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CreditCardIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">
          {family.familyBankAccountNumber}
        </Typography>
      </Box>
    )}
  </Box>
</Box>


          <Grid container spacing={1}>
            {family.products.map((product) => (
              <Grid item xs={12} key={product.id}>
                <Card
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: isMobile ? 1 : 2,
                    }}
                  >
                    <Avatar
                      src={product.image}
                      variant="rounded"
                      sx={{
                        width: isSmallMobile ? 50 : 60,
                        height: isSmallMobile ? 50 : 60,
                        mr: isRtl ? 0 : isMobile ? 1 : 2,
                        ml: isRtl ? (isMobile ? 1 : 2) : 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant={isMobile ? "body2" : "subtitle1"}
                        fontWeight="bold"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {product.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: isSmallMobile ? "column" : "row",
                          justifyContent: "space-between",
                          mt: 0.5,
                          gap: isSmallMobile ? 0.5 : 0,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {t("orders.quantity")}: {product.quantity}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
                        >
                          {t("orders.unitPrice")} : {product.price?.toFixed(2)}{" "}
                          <span>{t("rs")}</span>
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {t("orders.totalPrice")} :{" "}
                          {(product.price * product.quantity)?.toFixed(2)}{" "}
                          <span>{t("rs")}</span>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* <CardContent
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[900]
                          : theme.palette.grey[50],
                      borderTop: `1px solid ${theme.palette.divider}`,
                      pt: 0,
                      pb: "8px !important",
                      px: isMobile ? 1 : 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontSize: isMobile ? "0.8rem" : "0.875rem",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {product.description}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: isSmallMobile ? "column" : "row",
                        justifyContent: "space-between",
                        alignItems: isSmallMobile ? "flex-start" : "center",
                        gap: isSmallMobile ? 0.5 : 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
                      >
                        {t("orders.unitPrice")} : {product.price}{" "}
                        <span>{t("rs")}</span>
                      </Typography>
                    </Box>
                  </CardContent> */}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Collapse>
  </Paper>
);

export default MyOrders;
